/**
 * AuthConfigStep
 *
 * Shown after the spec preview succeeds, before the user hits "Generate MCP".
 * Reads spec.auth_schemes and renders the exact credential fields that API needs.
 *
 * Auth types handled:
 *   http/basic  → Email + API Token
 *   http/bearer → API Token / Secret key (e.g. OpenAI, Stripe)
 *   apiKey      → API Key (header or query)
 *   oauth2      → informational note only (not supported for hosted MCPs)
 *   none        → renders nothing (no auth required)
 *
 * Credentials are passed up via onChange and sent encrypted to the backend.
 */

import { motion } from "framer-motion";
import { Lock, ShieldCheck } from "lucide-react";

export type AuthScheme = {
  type: string;       // "http" | "apiKey" | "oauth2" | "openIdConnect"
  scheme?: string;    // "basic" | "bearer" (only for type=http)
  name?: string;      // header/query param name (for apiKey)
  location?: string;  // "header" | "query" (for apiKey)
  is_complex?: boolean;
  /** From OpenAPI oauth2 flows (preview API). */
  authorization_url?: string | null;
  token_url?: string | null;
  scopes?: string[];
};

export type Credentials = {
  // Basic auth
  username?: string;
  password?: string;
  // Bearer / API key
  api_key?: string;
  // OAuth 2.0 — encrypted token blob from /api/v1/oauth/exchange.
  // This is NEVER the raw access token — always the Fernet-encrypted blob.
  // Passed as-is to /api/v1/host which decrypts it server-side.
  oauth_access_token_enc?: string;
};

type Props = {
  authSchemes: AuthScheme[];
  credentials: Credentials;
  onChange: (creds: Credentials) => void;
  /** User chose to host without storing credentials. */
  skipped?: boolean;
  onSkip?: () => void;
  onResume?: () => void;
  /** Render inside a parent card (no outer glass wrapper). */
  embedded?: boolean;
  /** Separator when stacked below another setup block in the same card. */
  withTopDivider?: boolean;
};

export function detectAuthType(schemes: AuthScheme[]): "basic" | "bearer" | "apiKey" | "oauth2" | "none" {
  if (!schemes || schemes.length === 0) return "none";

  // Prefer actionable types (basic > bearer > apiKey) over oauth2.
  // Normalize scheme to lowercase for comparison — some specs use "Bearer", "Basic", etc.
  const normalize = (s: string | undefined | null) => (s || "").toLowerCase().trim();

  if (schemes.some(s => s.type === "http" && normalize(s.scheme) === "basic")) return "basic";
  if (schemes.some(s => s.type === "http" && normalize(s.scheme) === "bearer")) return "bearer";
  if (schemes.some(s => s.type === "apiKey")) return "apiKey";
  if (schemes.some(s => s.type === "oauth2" || s.type === "openIdConnect")) return "oauth2";

  // Fallback: type="http" with a missing or unrecognised scheme → treat as bearer.
  // This covers specs that write `type: http` but omit the scheme subfield entirely,
  // or use non-standard values like "token" or "apiToken".
  if (schemes.some(s => s.type === "http")) return "bearer";

  return "none";
}

function getApiKeyHeaderName(schemes: AuthScheme[]): string {
  const s = schemes.find(s => s.type === "apiKey");
  return s?.name || "X-API-Key";
}

function AuthConfigFields({
  authSchemes,
  credentials,
  onChange,
  skipped = false,
  onSkip,
  onResume,
}: Pick<Props, "authSchemes" | "credentials" | "onChange" | "skipped" | "onSkip" | "onResume">) {
  const authType = detectAuthType(authSchemes);

  if (skipped) {
    return (
      <div className="flex items-center justify-between gap-3 py-1">
        <p className="text-xs text-muted-foreground leading-relaxed">
          No API key — host now and add credentials later if the API requires them.
        </p>
        {onResume ? (
          <button
            type="button"
            onClick={onResume}
            className="text-xs font-medium text-foreground hover:underline underline-offset-2 shrink-0"
          >
            Add key
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <>
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-muted flex-shrink-0">
              <Lock className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground leading-tight">
                API credentials
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Stored encrypted · never visible in your MCP link
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {onSkip ? (
                <button
                  type="button"
                  onClick={onSkip}
                  className="text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Skip API key
                </button>
              ) : null}
              <span className="hidden sm:flex items-center gap-1 text-[11px] text-muted-foreground">
                <ShieldCheck className="w-3.5 h-3.5" />
                Encrypted
              </span>
            </div>
          </div>

          {/* ── Basic Auth ── */}
          {authType === "basic" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Email / Username
                </label>
                <input
                  type="email"
                  autoComplete="off"
                  placeholder="you@company.com"
                  value={credentials.username || ""}
                  onChange={e => onChange({ ...credentials, username: e.target.value })}
                  className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/20 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                  API Token
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder="Paste your API token"
                  value={credentials.password || ""}
                  onChange={e => onChange({ ...credentials, password: e.target.value })}
                  className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/20 transition font-mono"
                />
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  Create an API token in your provider&apos;s developer or security settings, then paste it here.
                </p>
              </div>
            </div>
          )}

          {/* ── Bearer Token (e.g. OpenAI, Stripe) ── */}
          {authType === "bearer" && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                API Token / Secret key
              </label>
              <input
                type="password"
                autoComplete="new-password"
                placeholder="sk-… or your API token"
                value={credentials.api_key || ""}
                onChange={e => onChange({ ...credentials, api_key: e.target.value })}
                className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/20 transition font-mono"
              />
            </div>
          )}

          {/* ── API Key (header or query) ── */}
          {authType === "apiKey" && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                API Key{" "}
                <span className="text-muted-foreground/60 font-normal">
                  (sent as {getApiKeyHeaderName(authSchemes)} header)
                </span>
              </label>
              <input
                type="password"
                autoComplete="new-password"
                placeholder="Paste your API key"
                value={credentials.api_key || ""}
                onChange={e => onChange({ ...credentials, api_key: e.target.value })}
                className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/20 transition font-mono"
              />
            </div>
          )}

          {/* ── OAuth2 — handled by OAuthConnectButton above this component ── */}
          {/* This branch is intentionally empty: the OAuth connect UI is rendered
              by OAuthConnectButton in preview.tsx, not here. This component only
              handles static credential entry (bearer/basic/apiKey). */}
          {authType === "oauth2" && null}
    </>
  );
}

export default function AuthConfigStep({
  authSchemes,
  credentials,
  onChange,
  skipped = false,
  onSkip,
  onResume,
  embedded = false,
  withTopDivider = false,
}: Props) {
  const authType = detectAuthType(authSchemes);

  if (authType === "none") return null;

  const fields = (
    <AuthConfigFields
      authSchemes={authSchemes}
      credentials={credentials}
      onChange={onChange}
      skipped={skipped}
      onSkip={onSkip}
      onResume={onResume}
    />
  );

  if (embedded) {
    return (
      <div className={withTopDivider ? "pt-6 border-t border-border" : undefined}>{fields}</div>
    );
  }

  return (
    <motion.section
      initial={false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="py-0 px-6"
    >
      <div className="container max-w-2xl mx-auto">
        <div className="glass-strong rounded-2xl p-7 shadow-soft">{fields}</div>
      </div>
    </motion.section>
  );
}

/**
 * Returns true if the auth type requires credentials and they are filled in.
 *
 * OAuth: credentials are complete when the Fernet-encrypted token blob
 * (oauth_access_token_enc) is present. The actual token exchange happened
 * in OAuthConnectButton and the encrypted blob was stored in creds.
 */
export function credentialsComplete(authSchemes: AuthScheme[], creds: Credentials): boolean {
  const t = detectAuthType(authSchemes);
  if (t === "none") return true;
  // OAuth: complete when we have an encrypted token from the OAuth flow
  if (t === "oauth2") return !!(creds.oauth_access_token_enc?.trim());
  if (t === "basic") return !!(creds.username?.trim() && creds.password?.trim());
  if (t === "bearer" || t === "apiKey") return !!(creds.api_key?.trim());
  return true;
}
